import * as XLSX from "xlsx";

import type {
  Account,
  BudgetImportRow,
  BudgetParseResult,
} from "~/types/contabilidad";

const ELIGIBLE_ACCOUNT_TYPES = ["ingreso", "gasto"];

/** Lowercase, trim, strip accents and collapse spaces for header matching. */
function normalizeHeader(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

// Header aliases. The importer validates purely by account CODE — the name
// column is optional and never used for validation (accountant observation #3).
const CODE_PRIMARY_HEADERS = [
  "codigo",
  "codigo cuenta",
  "codigo de cuenta",
  "codigo de la cuenta",
  "cod",
  "cod cuenta",
];
const CODE_FALLBACK_HEADERS = [
  "cuenta",
  "cuenta contable",
  "nro cuenta",
  "numero cuenta",
  "numero de cuenta",
  "n cuenta",
];
const NAME_HEADERS = [
  "nombre",
  "nombre cuenta",
  "nombre de cuenta",
  "descripcion",
  "glosa",
  "detalle",
];

const SHORT_MONTHS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];
const FULL_MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** Map a header cell to a 0-based month index (Ene..Dic), or -1. */
function monthIndexForHeader(value: unknown): number {
  const n = normalizeHeader(value);
  if (n === "") return -1;
  let idx = SHORT_MONTHS.indexOf(n);
  if (idx !== -1) return idx;
  idx = FULL_MONTHS.indexOf(n);
  if (idx !== -1) return idx;
  if (n === "sept") return 8;
  const num = Number(n);
  if (Number.isInteger(num) && num >= 1 && num <= 12) return num - 1;
  return -1;
}

interface HeaderLayout {
  headerIndex: number;
  codeCol: number;
  nameCol: number; // -1 when absent
  monthCols: number[]; // 12 entries, -1 when a month column is absent
}

/**
 * Detect the header row and the code / name / month columns by header text,
 * so layout is position-independent (a file with just Codigo + Ene..Dic parses
 * the same as Codigo + Cuenta + Ene..Dic).
 */
function detectLayout(rawRows: unknown[][]): HeaderLayout | null {
  for (let r = 0; r < Math.min(rawRows.length, 25); r++) {
    const row = rawRows[r] ?? [];

    let codeCol = row.findIndex((cell) =>
      CODE_PRIMARY_HEADERS.includes(normalizeHeader(cell))
    );
    if (codeCol === -1) {
      codeCol = row.findIndex((cell) =>
        CODE_FALLBACK_HEADERS.includes(normalizeHeader(cell))
      );
    }
    if (codeCol === -1) continue;

    const nameCol = row.findIndex(
      (cell, i) => i !== codeCol && NAME_HEADERS.includes(normalizeHeader(cell))
    );

    const monthCols = Array(12).fill(-1) as number[];
    row.forEach((cell, i) => {
      if (i === codeCol || i === nameCol) return;
      const m = monthIndexForHeader(cell);
      if (m !== -1 && monthCols[m] === -1) monthCols[m] = i;
    });

    const found = monthCols.filter((c) => c !== -1).length;
    if (found === 0) {
      // No labelled months: assume the 12 columns after the code column
      // (skipping an optional name column) are Ene..Dic.
      let start = codeCol + 1;
      if (start === nameCol) start += 1;
      for (let m = 0; m < 12; m++) monthCols[m] = start + m;
    }

    return { headerIndex: r, codeCol, nameCol, monthCols };
  }
  return null;
}

/**
 * Parse a numeric cell tolerating Chilean formats: "120.000.000" (thousand
 * dots), "1.234,5" (decimal comma), plain numbers, and "$" prefixes.
 * Returns null when the value is not a number.
 */
function parseAmountCell(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let text = String(value).trim().replace(/\$/g, "").replace(/\s/g, "");
  if (text === "" || text === "-") return 0;

  if (text.includes(".") && text.includes(",")) {
    // "1.234.567,89" -> dots are thousands, comma is decimal
    text = text.replace(/\./g, "").replace(",", ".");
  } else if (text.includes(",")) {
    // "1234,5" -> comma is decimal
    text = text.replace(",", ".");
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
    // "120.000.000" -> dots are thousand separators
    text = text.replace(/\./g, "");
  }

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Parse a grid cell edited by the user; invalid input counts as 0.
 */
export function parseGridCellValue(value: string): number {
  const parsed = parseAmountCell(value);
  return parsed === null ? 0 : parsed;
}

/**
 * Parse a budget file (.xlsx/.xls/.csv). Columns are detected by header:
 * a code column (validated against the company's eligible accounts) and up to
 * 12 month columns (Ene..Dic). The name column is optional and never used for
 * validation. Amounts are stored exactly as entered (signed, estado-de-
 * resultados convention: ingresos +, costos/gastos -).
 */
export async function parseBudgetFile(
  file: File,
  companyAccounts: Account[]
): Promise<BudgetParseResult> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer);

  const sheetName =
    workbook.SheetNames.find((name) => name.toLowerCase() === "presupuesto") ||
    workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];

  const rawRows: unknown[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
  });

  const layout = detectLayout(rawRows);
  if (!layout) {
    return {
      rows: [],
      validRows: [],
      errorCount: 1,
      warningCount: 0,
    };
  }

  const { headerIndex, codeCol, nameCol, monthCols } = layout;

  const accountsByCode = new Map(
    companyAccounts.map((account) => [account.code.toLowerCase(), account])
  );
  const seenCodes = new Set<string>();

  const rows: BudgetImportRow[] = [];

  rawRows.slice(headerIndex + 1).forEach((rawRow, index) => {
    const code = String(rawRow[codeCol] ?? "").trim();
    const name = nameCol >= 0 ? String(rawRow[nameCol] ?? "").trim() : "";

    // Skip fully empty rows
    const hasAnyValue =
      code !== "" ||
      name !== "" ||
      monthCols.some((c) => c >= 0 && rawRow[c] !== "" && rawRow[c] !== null);
    if (!hasAnyValue) return;

    const row: BudgetImportRow = {
      rowNumber: headerIndex + index + 2, // 1-based, after the header row
      code,
      accountName: name,
      accountId: null,
      amounts: Array(12).fill(0),
      total: 0,
    };

    if (code === "") {
      row.error = "Fila sin código de cuenta";
      rows.push(row);
      return;
    }

    const account = accountsByCode.get(code.toLowerCase());
    if (!account) {
      row.error = "Código no existe en el plan de cuentas de la empresa";
      rows.push(row);
      return;
    }

    if (!ELIGIBLE_ACCOUNT_TYPES.includes(account.account_type)) {
      row.error = `Cuenta de tipo "${account.account_type}" — solo se presupuestan cuentas de ingreso o gasto`;
      rows.push(row);
      return;
    }

    if (seenCodes.has(code.toLowerCase())) {
      row.error = "Código duplicado en el archivo";
      rows.push(row);
      return;
    }
    seenCodes.add(code.toLowerCase());

    row.accountId = account.id;
    row.accountName = account.name;

    const amounts: number[] = [];
    let invalidMonth: number | null = null;
    let hasUnusualSign = false;

    for (let month = 0; month < 12; month++) {
      const col = monthCols[month];
      const parsed = parseAmountCell(col >= 0 ? rawRow[col] : "");
      if (parsed === null) {
        invalidMonth = month + 1;
        break;
      }
      // Signs are stored as entered (P&L convention). Flag — but don't block —
      // a line whose sign contradicts its account type (likely a typo).
      if (
        (account.account_type === "ingreso" && parsed < 0) ||
        (account.account_type === "gasto" && parsed > 0)
      ) {
        hasUnusualSign = true;
      }
      amounts.push(parsed);
    }

    if (invalidMonth !== null) {
      row.error = `Valor no numérico en el mes ${invalidMonth}`;
      rows.push(row);
      return;
    }

    row.amounts = amounts;
    row.total = amounts.reduce((sum, amount) => sum + amount, 0);
    if (hasUnusualSign) {
      row.warning =
        account.account_type === "ingreso"
          ? "Monto negativo en una cuenta de ingreso — revise el signo"
          : "Monto positivo en una cuenta de gasto — revise el signo";
    }

    rows.push(row);
  });

  const validRows = rows.filter((row) => !row.error);

  return {
    rows,
    validRows,
    errorCount: rows.filter((row) => row.error).length,
    warningCount: rows.filter((row) => row.warning).length,
  };
}
